import type { StudioNodeType } from './types'

export interface ModelParamField {
  key: string
  labelKey: string
  type: 'select' | 'number' | 'readonly' | 'text'
  options?: { value: string; labelKey: string }[]
  min?: number
  max?: number
  step?: number
  default: string | number
}

const ASPECT_OPTIONS = [
  { value: '9:16', labelKey: 'canvas.params.aspect916' },
  { value: '16:9', labelKey: 'canvas.params.aspect169' },
  { value: '1:1', labelKey: 'canvas.params.aspect11' },
  { value: '4:3', labelKey: 'canvas.params.aspect43' },
  { value: '3:4', labelKey: 'canvas.params.aspect34' },
]

const RESOLUTION_OPTIONS = [
  { value: '512', labelKey: '512' },
  { value: '768', labelKey: '768' },
  { value: '1024', labelKey: '1024' },
  { value: '1536', labelKey: '1536' },
]

const BATCH_COUNT_OPTIONS = [
  { value: '1', labelKey: 'canvas.params.batch1' },
  { value: '2', labelKey: 'canvas.params.batch2' },
  { value: '4', labelKey: 'canvas.params.batch4' },
]

const VIDEO_DURATION_SEC = [3, 5, 10, 15, 20, 25, 30] as const

const VIDEO_DURATION_LABEL_KEYS: Record<(typeof VIDEO_DURATION_SEC)[number], string> = {
  3: 'canvas.params.duration3',
  5: 'canvas.params.duration5',
  10: 'canvas.params.duration10',
  15: 'canvas.params.duration15s',
  20: 'canvas.params.duration20s',
  25: 'canvas.params.duration25s',
  30: 'canvas.params.duration30s',
}

const DURATION_OPTIONS = VIDEO_DURATION_SEC.map((sec) => ({
  value: String(sec),
  labelKey: VIDEO_DURATION_LABEL_KEYS[sec],
}))

function normalizeVideoDurationSec(raw: unknown): number {
  const sec = Number(raw)
  if (!Number.isFinite(sec) || sec <= 0) return 5
  if ((VIDEO_DURATION_SEC as readonly number[]).includes(sec)) return sec
  return VIDEO_DURATION_SEC.reduce((best, opt) =>
    Math.abs(opt - sec) < Math.abs(best - sec) ? opt : best,
  5)
}

const MOTION_OPTIONS = [
  { value: 'low', labelKey: 'canvas.params.motionLow' },
  { value: 'medium', labelKey: 'canvas.params.motionMedium' },
  { value: 'high', labelKey: 'canvas.params.motionHigh' },
]

const AUDIO_MODE_OPTIONS = [
  { value: 'tts', labelKey: 'canvas.params.audioModeTts' },
  { value: 'bgm', labelKey: 'canvas.params.audioModeBgm' },
  { value: 'sfx', labelKey: 'canvas.params.audioModeSfx' },
]

const VOICE_OPTIONS = [
  { value: 'male', labelKey: 'canvas.params.voiceMale' },
  { value: 'female', labelKey: 'canvas.params.voiceFemale' },
  { value: 'narrator', labelKey: 'canvas.params.voiceNarrator' },
  { value: 'child', labelKey: 'canvas.params.voiceChild' },
]

const GEN_IMAGE_PARAMS: ModelParamField[] = [
  { key: 'batch_count', labelKey: 'canvas.params.batchCount', type: 'select', options: BATCH_COUNT_OPTIONS, default: '1' },
  { key: 'aspect_ratio', labelKey: 'canvas.params.aspect', type: 'select', options: ASPECT_OPTIONS, default: '9:16' },
  { key: 'resolution', labelKey: 'canvas.params.resolution', type: 'select', options: RESOLUTION_OPTIONS, default: '1024' },
  { key: 'steps', labelKey: 'canvas.params.steps', type: 'number', min: 4, max: 60, default: 28 },
  { key: 'cfg', labelKey: 'canvas.params.cfg', type: 'number', min: 1, max: 20, default: 7 },
  { key: 'seed', labelKey: 'canvas.params.seed', type: 'number', min: 0, max: 4294967295, default: 0 },
  { key: 'strength', labelKey: 'canvas.params.strength', type: 'number', min: 0, max: 1, default: 0.6 },
  { key: 'denoise', labelKey: 'canvas.params.denoise', type: 'number', min: 0, max: 1, default: 0.5 },
  { key: 'controlnet', labelKey: 'canvas.params.controlnet', type: 'select', options: [
    { value: 'none', labelKey: 'canvas.params.cnNone' },
    { value: 'canny', labelKey: 'canvas.params.cnCanny' },
    { value: 'openpose', labelKey: 'canvas.params.cnOpenpose' },
    { value: 'depth', labelKey: 'canvas.params.cnDepth' },
  ], default: 'none' },
]

const GEN_VIDEO_PARAMS: ModelParamField[] = [
  { key: 'aspect_ratio', labelKey: 'canvas.params.aspect', type: 'select', options: ASPECT_OPTIONS, default: '9:16' },
  { key: 'duration', labelKey: 'canvas.params.duration', type: 'select', options: DURATION_OPTIONS, default: '5' },
  { key: 'with_audio', labelKey: 'canvas.params.withAudio', type: 'select', options: [
    { value: '1', labelKey: 'canvas.params.yes' },
    { value: '0', labelKey: 'canvas.params.no' },
  ], default: '1' },
  { key: 'motion', labelKey: 'canvas.params.motion', type: 'select', options: MOTION_OPTIONS, default: 'medium' },
  { key: 'fps', labelKey: 'canvas.params.fps', type: 'number', min: 8, max: 60, default: 24 },
  { key: 'seed', labelKey: 'canvas.params.seed', type: 'number', min: 0, max: 4294967295, default: 0 },
  { key: 'motion_strength', labelKey: 'canvas.params.motionStrength', type: 'number', min: 0, max: 1, default: 0.5 },
  { key: 'fix_first_frame', labelKey: 'canvas.params.fixFirstFrame', type: 'select', options: [
    { value: '0', labelKey: 'canvas.params.no' },
    { value: '1', labelKey: 'canvas.params.yes' },
  ], default: '0' },
  { key: 'fix_last_frame', labelKey: 'canvas.params.fixLastFrame', type: 'select', options: [
    { value: '0', labelKey: 'canvas.params.no' },
    { value: '1', labelKey: 'canvas.params.yes' },
  ], default: '0' },
]

export const GEN_AUDIO_PARAMS: ModelParamField[] = [
  { key: 'mode', labelKey: 'canvas.params.audioMode', type: 'select', options: AUDIO_MODE_OPTIONS, default: 'tts' },
  { key: 'voice', labelKey: 'canvas.params.voice', type: 'select', options: VOICE_OPTIONS, default: 'female' },
  { key: 'duration', labelKey: 'canvas.params.duration', type: 'number', min: 1, max: 300, default: 10 },
  { key: 'speed', labelKey: 'canvas.params.speed', type: 'number', min: 0.5, max: 2, default: 1 },
  { key: 'pitch', labelKey: 'canvas.params.pitch', type: 'number', min: -12, max: 12, default: 0 },
]

/** 从该 key 起（含）归入「更多参数」折叠区 */
const ADVANCED_PARAM_FROM: Partial<Record<StudioNodeType, string>> = {
  gen_image: 'steps',
  gen_video: 'motion',
  gen_audio: 'duration',
}

export function primaryParamFieldsForNode(nodeType: StudioNodeType): ModelParamField[] {
  const all = paramFieldsForNode(nodeType)
  const fromKey = ADVANCED_PARAM_FROM[nodeType]
  if (!fromKey) return all
  const idx = all.findIndex((f) => f.key === fromKey)
  return idx === -1 ? all : all.slice(0, idx)
}

export function advancedParamFieldsForNode(nodeType: StudioNodeType): ModelParamField[] {
  const all = paramFieldsForNode(nodeType)
  const fromKey = ADVANCED_PARAM_FROM[nodeType]
  if (!fromKey) return []
  const idx = all.findIndex((f) => f.key === fromKey)
  return idx === -1 ? [] : all.slice(idx)
}

/** 按节点类型获取参数字段定义 */
export function paramFieldsForNode(nodeType: StudioNodeType): ModelParamField[] {
  switch (nodeType) {
    case 'gen_image':
      return GEN_IMAGE_PARAMS
    case 'gen_video':
      return GEN_VIDEO_PARAMS
    case 'gen_audio':
      return GEN_AUDIO_PARAMS
    default:
      return []
  }
}

/** 兼容旧调用：按 AITaskType 取参数（迁移期使用） */
export function paramFieldsForLegacyTask(type: 'image' | 'video'): ModelParamField[] {
  return type === 'video' ? GEN_VIDEO_PARAMS : GEN_IMAGE_PARAMS
}

export function mergeGenParams(
  nodeType: StudioNodeType,
  params?: Record<string, string | number>,
): Record<string, string | number> {
  const out: Record<string, string | number> = { ...defaultParamsForNode(nodeType), ...params }
  if (nodeType === 'gen_video') {
    const sec = normalizeVideoDurationSec(out.duration ?? out.duration_sec)
    out.duration = String(sec)
    delete out.duration_sec
    delete out.durationSec
  }
  return out
}

/** 提交视频任务前：画布 duration → 后端 duration_sec（整型） */
export function normalizeVideoSubmitPayload(
  payload: Record<string, unknown>,
): Record<string, unknown> {
  const out = { ...payload }
  out.duration_sec = normalizeVideoDurationSec(out.duration ?? out.duration_sec)
  delete out.duration
  delete out.durationSec
  return out
}

/** 生成时合并 UI 默认值，避免下拉显示与 gen.params 不同步 */
export function resolvedGenParamsForSubmit(
  params: Record<string, string | number> | undefined,
  fields: ModelParamField[],
): Record<string, string | number> {
  const out: Record<string, string | number> = { ...(params ?? {}) }
  delete (out as Record<string, unknown>).duration_sec
  delete (out as Record<string, unknown>).durationSec
  for (const f of fields) {
    const raw = out[f.key]
    if (raw === undefined || raw === '') {
      out[f.key] = f.default
    }
  }
  return out
}

/** 视频节点提交 payload：合并参数 + duration_sec + 剔除画布内部键 */
export function buildVideoSubmitPayload(
  params: Record<string, string | number> | undefined,
  fields: ModelParamField[],
  extras: Record<string, unknown> = {},
): Record<string, unknown> {
  const merged = mergeGenParams('gen_video', resolvedGenParamsForSubmit(params, fields))
  const strippedExtras: Record<string, unknown> = { ...extras }
  for (const f of fields) {
    delete strippedExtras[f.key]
  }
  delete strippedExtras.duration_sec
  delete strippedExtras.durationSec
  const withExtras: Record<string, unknown> = { ...strippedExtras, ...merged }
  return normalizeVideoSubmitPayload(withExtras)
}

export function defaultParamsForNode(nodeType: StudioNodeType): Record<string, string | number> {
  const fields = paramFieldsForNode(nodeType)
  const out: Record<string, string | number> = {}
  for (const f of fields) out[f.key] = f.default
  return out
}
